import { MAX_SELECTABLE_SERIES } from "~/feature/chart/constants";
import { EMBED_MAX_SIZE } from "~/feature/embed/constants";
import type { IEmbedConfig } from "~/feature/embed/types";
import type { IEmbedSize } from "~/feature/embed/utils/getEmbedMinSize";

const MARKET_SEPARATOR = ",";

// One line per market — the same cap the chart's picker applies.
export const MAX_EMBED_LINES = MAX_SELECTABLE_SERIES;

// The slug is spliced into a path on the internal API, so it may only ever be
// a slug — anything with a separator, a query or an encoding in it is refused.
const EVENT_SLUG = /^[A-Za-z0-9_-]+$/;
const MARKET_ID = /^[A-Za-z0-9_-]{1,64}$/;
const DIMENSION = /^\d+$/;

const FLAG_DEFAULTS = {
  chart: true,
  odds: true,
  consensus: false,
  border: true,
  dark: false,
} as const;

type FlagName = keyof typeof FLAG_DEFAULTS;

const parseFlag = (search: URLSearchParams, name: FlagName): boolean | null => {
  const raw = search.get(name);

  if (raw === null) return FLAG_DEFAULTS[name];
  if (raw === "true") return true;
  if (raw === "false") return false;

  return null;
};

const parseMarketIds = (raw: string): string[] | null => {
  const ids = raw
    .split(MARKET_SEPARATOR)
    .map((id) => id.trim())
    .filter(Boolean);

  if (!ids.length) return null;
  if (ids.some((id) => !MARKET_ID.test(id))) return null;

  const unique = [...new Set(ids)];

  return unique.length > MAX_EMBED_LINES ? null : unique;
};

const parseDimension = (raw: string | null, max: number): number | null => {
  if (raw === null) return null;
  if (!DIMENSION.test(raw)) return null;

  const value = Number(raw);

  return value > 0 && value <= max ? value : null;
};

const parseSize = (search: URLSearchParams): IEmbedSize | null | false => {
  const rawWidth = search.get("w");
  const rawHeight = search.get("h");

  if (rawWidth === null && rawHeight === null) return null;

  const width = parseDimension(rawWidth, EMBED_MAX_SIZE.width);
  const height = parseDimension(rawHeight, EMBED_MAX_SIZE.height);

  if (width === null || height === null) return false;

  return { width, height };
};

export const parseEmbedDarkMode = (search: URLSearchParams): boolean =>
  parseFlag(search, "dark") === true;

export const parseEmbedParams = (
  search: URLSearchParams
): IEmbedConfig | null => {
  const eventSlug = search.get("event")?.trim();
  const markets = search.get("markets")?.trim();

  if (!eventSlug || !EVENT_SLUG.test(eventSlug) || !markets) return null;

  const marketIds = parseMarketIds(markets);
  const chart = parseFlag(search, "chart");
  const odds = parseFlag(search, "odds");
  const consensusOnly = parseFlag(search, "consensus");
  const border = parseFlag(search, "border");
  const darkMode = parseFlag(search, "dark");
  const size = parseSize(search);

  if (
    !marketIds ||
    chart === null ||
    odds === null ||
    consensusOnly === null ||
    border === null ||
    darkMode === null ||
    size === false
  ) {
    return null;
  }

  return {
    eventSlug,
    marketIds,
    chart,
    odds,
    consensusOnly,
    border,
    darkMode,
    size,
  };
};

export const buildEmbedSearch = ({
  eventSlug,
  marketIds,
  chart,
  odds,
  consensusOnly,
  border,
  darkMode,
  size,
}: IEmbedConfig): URLSearchParams =>
  new URLSearchParams({
    event: eventSlug,
    markets: marketIds.join(MARKET_SEPARATOR),
    chart: String(chart),
    odds: String(odds),
    consensus: String(consensusOnly),
    border: String(border),
    dark: String(darkMode),
    ...(size && { w: String(size.width), h: String(size.height) }),
  });
